<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import KPIService from '@/service/kpi.service';
import type {
  ActivationMetrics, GoalConfig, RetentionMetrics, WeeklyAcquisitionMetrics, WeeklyRequestsMetrics, TopServiceWeeklyStat, PlatformBreakdown, AcquisitionChannelBreakdown,
} from '@/types/kpi';

const loading = ref(true);
const weeks = ref(8);
const commissionRate = ref(0.15);

const weeklyInstalls = ref<WeeklyAcquisitionMetrics[]>([]);
const activation = ref<ActivationMetrics[]>([]);
const retention = ref<RetentionMetrics[]>([]);
const monetization = ref<{ gmv: number; netRevenue: number }>({ gmv: 0, netRevenue: 0 });
const topline = ref<{ totalUsers: number; totalRequests: number; paidRequests: number; conversionPaidPct: number }>({
  totalUsers: 0, totalRequests: 0, paidRequests: 0, conversionPaidPct: 0,
});
const weeklyRequests = ref<WeeklyRequestsMetrics[]>([]);
const topServices = ref<TopServiceWeeklyStat[]>([]);
const platform = ref<PlatformBreakdown>({ ios: 0, android: 0 });
const paidServicesThisWeek = ref<{ distinctCount: number; services: Array<{ serviceId: string; serviceTitle?: string; paid: number }> }>({ distinctCount: 0, services: [] });
const acquisitionChannels = ref<AcquisitionChannelBreakdown>({});

const goals = ref<GoalConfig>({
  weeklyInstallsTarget: 200,
  activationRateTargetPct: 35,
  wauTarget: 300,
  monthlyBookersTargetPct: 15,
  gmvTarget: 5000,
  netRevenueTarget: 750,
});

const latestInstalls = computed(() => weeklyInstalls.value.at(-1)?.installs ?? 0);
const prevInstalls = computed(() => weeklyInstalls.value.at(-2)?.installs ?? 0);
const installsWoW = computed(() => (prevInstalls.value ? (((latestInstalls.value - prevInstalls.value) / prevInstalls.value) * 100).toFixed(1) : '0'));

const latestActivation = computed(() => activation.value.at(-1)?.activationRatePct ?? 0);
const prevActivation = computed(() => activation.value.at(-2)?.activationRatePct ?? 0);
const activationWoW = computed(() => (prevActivation.value ? (((latestActivation.value - prevActivation.value) / prevActivation.value) * 100).toFixed(1) : '0'));

const latestWAU = computed(() => retention.value.at(-1)?.wau ?? 0);
const latestMonthlyBookersPct = computed(() => retention.value.at(-1)?.monthlyBookersPct ?? 0);

const latestWeeklyRequests = computed(() => weeklyRequests.value.at(-1) || { total: 0, paid: 0, weekLabel: '' });
const maxWeeklyRequests = computed(() => Math.max(1, ...weeklyRequests.value.map((x) => x.total)));
const maxAcquisitionChannel = computed(() => {
  const values = Object.values(acquisitionChannels.value || {});
  if (!values.length) return 1;
  return Math.max(1, ...values);
});

function statusPill(current: number, target: number) {
  if (current >= target) return { text: 'On Track', cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-500/40' };
  const gap = (target - current) / target;
  if (gap <= 0.1) return { text: 'At Risk', cls: 'bg-yellow-900/40 text-yellow-300 border-yellow-500/40' };
  return { text: 'Off Track', cls: 'bg-red-900/40 text-red-300 border-red-500/40' };
}

async function refreshAll() {
  loading.value = true;
  [weeklyInstalls.value, activation.value, retention.value, weeklyRequests.value] = await Promise.all([
    KPIService.getWeeklyInstalls(weeks.value),
    KPIService.getActivationRates(weeks.value, 7),
    KPIService.getRetentionMetrics(weeks.value),
    KPIService.getWeeklyRequests(weeks.value),
  ]);
  monetization.value = await KPIService.getMonetization(30, commissionRate.value);
  topline.value = await KPIService.getTopline();
  topServices.value = await KPIService.getTopServicesWeekly(5);
  platform.value = await KPIService.getPlatformBreakdown();
  paidServicesThisWeek.value = await KPIService.getPaidServicesThisWeek();
  acquisitionChannels.value = await KPIService.getAcquisitionChannelBreakdown();
  loading.value = false;
}

async function loadGoals() {
  goals.value = await KPIService.getGoalsConfig();
}

async function saveGoals() {
  await KPIService.saveGoalsConfig(goals.value);
}

onMounted(async () => {
  await loadGoals();
  await refreshAll();
});
</script>

<template>
  <div class="dashboard-root text-white font-inter p-6">
    <div class="max-w-7xl mx-auto">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-gold mb-2">KPIs & Goals</h1>
          <p class="text-lg text-gray-300">Acquisition, Activation, Retention, Monetization</p>
        </div>
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-300">Weeks</label>
          <select v-model.number="weeks" class="glass-input" @change="refreshAll()">
            <option :value="4">4</option>
            <option :value="8">8</option>
            <option :value="12">12</option>
          </select>
          <label class="text-sm text-gray-300 ml-4">Commission</label>
          <input type="number" step="0.01" v-model.number="commissionRate" class="w-24 glass-input" @change="refreshAll()" />
        </div>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-gold border-t-transparent"></div>
      </div>

      <div v-else class="space-y-8">

        <!-- Topline -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="glass-card stat-card">
            <div class="text-sm opacity-80">Total Users</div>
            <div class="text-3xl font-bold">{{ topline.totalUsers }}</div>
          </div>
          <div class="glass-card stat-card">
            <div class="text-sm opacity-80">Total Requests</div>
            <div class="text-3xl font-bold">{{ topline.totalRequests }}</div>
          </div>
          <div class="glass-card stat-card">
            <div class="text-sm opacity-80">Paid Requests</div>
            <div class="text-3xl font-bold">{{ topline.paidRequests }}</div>
          </div>
          <div class="glass-card stat-card">
            <div class="text-sm opacity-80">Paid Conversion</div>
            <div class="text-3xl font-bold">{{ topline.conversionPaidPct }}%</div>
          </div>
        </div>

        <!-- Acquisition & Activation -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Acquisition</h2>
                <div class="text-sm text-gray-400">Weekly Installs</div>
              </div>
              <span :class="['text-xs px-2 py-1 rounded-full border', statusPill(latestInstalls, goals.weeklyInstallsTarget).cls]">
                {{ statusPill(latestInstalls, goals.weeklyInstallsTarget).text }}
              </span>
            </div>
            <div class="mt-3 text-3xl font-bold">{{ latestInstalls }}</div>
            <div class="text-sm text-gray-400">WoW: {{ installsWoW }}%</div>
            <div class="mt-4 space-y-2">
              <div v-for="pt in weeklyInstalls" :key="pt.weekLabel" class="flex items-center gap-3">
                <div class="w-28 text-xs text-gray-400">{{ pt.weekLabel }}</div>
                <div class="flex-1 glass-track">
                  <div class="glass-fill-gold" :style="{ width: (pt.installs / Math.max(1, Math.max(...weeklyInstalls.map(x=>x.installs)))) * 100 + '%' }"></div>
                </div>
                <div class="w-10 text-right text-sm">{{ pt.installs }}</div>
              </div>
            </div>
          </div>

          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Activation</h2>
                <div class="text-sm text-gray-400">% new users book within 7 days</div>
              </div>
              <span :class="['text-xs px-2 py-1 rounded-full border', statusPill(latestActivation, goals.activationRateTargetPct).cls]">
                {{ statusPill(latestActivation, goals.activationRateTargetPct).text }}
              </span>
            </div>
            <div class="mt-3 text-3xl font-bold">{{ latestActivation }}%</div>
            <div class="text-sm text-gray-400">WoW: {{ activationWoW }}%</div>
            <div class="mt-4 space-y-2">
              <div v-for="pt in activation" :key="pt.cohortLabel" class="flex items-center gap-3">
                <div class="w-28 text-xs text-gray-400">{{ pt.cohortLabel }}</div>
                <div class="flex-1 glass-track">
                  <div class="glass-fill-green" :style="{ width: pt.activationRatePct + '%' }"></div>
                </div>
                <div class="w-16 text-right text-sm">{{ pt.activationRatePct }}%</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Retention & Monetization -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Retention</h2>
                <div class="text-sm text-gray-400">WAU and monthly bookers %</div>
              </div>
              <span :class="['text-xs px-2 py-1 rounded-full border', statusPill(latestWAU, goals.wauTarget).cls]">
                {{ statusPill(latestWAU, goals.wauTarget).text }}
              </span>
            </div>
            <div class="mt-3 text-3xl font-bold">WAU: {{ latestWAU }}</div>
            <div class="text-sm text-gray-400">Monthly bookers: {{ latestMonthlyBookersPct }}%</div>
            <div class="mt-4 space-y-2">
              <div v-for="pt in retention" :key="pt.weekLabel" class="grid grid-cols-12 gap-3 items-center">
                <div class="col-span-3 text-xs text-gray-400">{{ pt.weekLabel }}</div>
                <div class="col-span-6 flex items-center gap-2">
                  <div class="flex-1 glass-track">
                    <div class="glass-fill-blue" :style="{ width: (pt.wau / Math.max(1, Math.max(...retention.map(x=>x.wau)))) * 100 + '%' }"></div>
                  </div>
                  <div class="w-12 text-right text-sm">{{ pt.wau }}</div>
                </div>
                <div class="col-span-3 text-right text-sm">{{ pt.monthlyBookersPct }}%</div>
              </div>
            </div>
          </div>

          <!-- <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Monetization</h2>
                <div class="text-sm text-gray-400">Last 30 days</div>
              </div>
              <span :class="['text-xs px-2 py-1 rounded-full border', statusPill(monetization.netRevenue, goals.netRevenueTarget).cls]">
                {{ statusPill(monetization.netRevenue, goals.netRevenueTarget).text }}
              </span>
            </div>
            <div class="mt-3 grid grid-cols-2 gap-4">
              <div>
                <div class="text-sm opacity-80">GMV</div>
                <div class="text-3xl font-bold">£{{ monetization.gmv.toFixed(2) }}</div>
              </div>
              <div>
                <div class="text-sm opacity-80">Net Revenue</div>
                <div class="text-3xl font-bold">£{{ monetization.netRevenue.toFixed(2) }}</div>
              </div>
            </div>
          </div> -->
           <!-- Paid Services This Week -->
        <div class="glass-card">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-2xl font-semibold text-gold">Paid Services (This Week)</h2>
              <div class="text-sm text-gray-400">Distinct services with at least one paid request</div>
            </div>
            <div class="text-3xl font-bold">{{ paidServicesThisWeek.distinctCount }}</div>
          </div>
          <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" v-if="paidServicesThisWeek.services.length">
            <div v-for="s in paidServicesThisWeek.services" :key="s.serviceId" class="flex items-center justify-between border border-zinc-700 rounded-lg px-3 py-2">
              <div class="font-medium">{{ s.serviceTitle || s.serviceId }}</div>
              <div class="text-sm text-gray-300">Paid: {{ s.paid }}</div>
            </div>
          </div>
          <div v-else class="text-sm text-gray-500 mt-2">No paid services yet this week.</div>
        </div>
        </div>

        <!-- Weekly Requests & Top Services -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Requests</h2>
                <div class="text-sm text-gray-400">Weekly total vs paid</div>
              </div>
            </div>
            <div class="mt-3 text-3xl font-bold">{{ latestWeeklyRequests.total }} <span class="text-base font-medium text-gray-400">(Paid: {{ latestWeeklyRequests.paid }})</span></div>
            <div class="mt-4 space-y-2">
              <div v-for="pt in weeklyRequests" :key="pt.weekLabel" class="flex items-center gap-3">
                <div class="w-28 text-xs text-gray-400">{{ pt.weekLabel }}</div>
                <div class="flex-1 glass-track relative">
                  <div class="glass-fill-blue" :style="{ width: (pt.total / maxWeeklyRequests) * 100 + '%' }"></div>
                  <div class="glass-fill-green absolute top-0 left-0" :style="{ width: (pt.paid / maxWeeklyRequests) * 100 + '%' }"></div>
                </div>
                <div class="w-20 text-right text-sm">{{ pt.paid }}/{{ pt.total }}</div>
              </div>
            </div>
          </div>

          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Top Services (This Week)</h2>
                <div class="text-sm text-gray-400">Most requested, with paid breakdown</div>
              </div>
            </div>
            <div class="mt-4 space-y-3">
              <div v-for="s in topServices" :key="s.serviceId" class="border border-zinc-700 rounded-lg p-3">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-semibold">{{ s.serviceTitle || s.serviceId }}</div>
                  <div class="text-sm text-gray-300">{{ s.paid }}/{{ s.total }} paid</div>
                </div>
                <div class="glass-track relative">
                  <div class="glass-fill-blue" :style="{ width: (s.total ? (s.total / Math.max(1, ...topServices.map(x=>x.total))) * 100 : 0) + '%' }"></div>
                  <div class="glass-fill-green absolute top-0 left-0" :style="{ width: (s.paid ? (s.paid / Math.max(1, ...topServices.map(x=>x.total))) * 100 : 0) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Users by Platform</h2>
                <div class="text-sm text-gray-400">iOS vs Android</div>
              </div>
            </div>
            <div class="mt-4 flex items-center gap-4">
              <div class="relative w-40 h-40">
                <svg viewBox="0 0 36 36" class="w-40 h-40">
                  <defs>
                    <linearGradient id="gradIOS" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="rgba(0,122,255,0.95)" />
                      <stop offset="100%" stop-color="rgba(0,122,255,0.6)" />
                    </linearGradient>
                    <linearGradient id="gradAndroid" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="rgba(52,199,89,0.95)" />
                      <stop offset="100%" stop-color="rgba(52,199,89,0.6)" />
                    </linearGradient>
                  </defs>
                  <circle cx="18" cy="18" r="15.915" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.16)" stroke-width="0.5" />
                  <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="url(#gradIOS)" stroke-width="3" stroke-dasharray="{{ (platform.ios / Math.max(1, (platform.ios + platform.android))) * 100 }}, 100" stroke-linecap="round" transform="rotate(-90 18 18)" />
                  <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="url(#gradAndroid)" stroke-width="3" stroke-dasharray="{{ (platform.android / Math.max(1, (platform.ios + platform.android))) * 100 }}, 100" stroke-dashoffset="-{{ (platform.ios / Math.max(1, (platform.ios + platform.android))) * 100 }}" stroke-linecap="round" transform="rotate(-90 18 18)" />
                </svg>
              </div>
              <div class="flex-1 space-y-2">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-3 h-3 rounded-sm" style="background: linear-gradient(90deg, rgba(0,122,255,0.95), rgba(0,122,255,0.6));"></span>
                    <span class="text-sm text-gray-300">iOS</span>
                  </div>
                  <div class="text-sm">{{ platform.ios }}</div>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-3 h-3 rounded-sm" style="background: linear-gradient(90deg, rgba(52,199,89,0.95), rgba(52,199,89,0.6));"></span>
                    <span class="text-sm text-gray-300">Android</span>
                  </div>
                  <div class="text-sm">{{ platform.android }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="glass-card">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-2xl font-semibold text-gold">Users by Acquisition Channel</h2>
                <div class="text-sm text-gray-400">Breakdown by acquisition source</div>
              </div>
            </div>
            <div class="mt-4 space-y-2" v-if="Object.keys(acquisitionChannels).length">
              <div
                v-for="[ch, cnt] in Object.entries(acquisitionChannels).sort((a, b) => b[1] - a[1])"
                :key="ch"
                class="flex items-center gap-3"
              >
                <div class="w-32 text-xs text-gray-300 truncate" :title="ch">{{ ch }}</div>
                <div class="flex-1 glass-track">
                  <div class="glass-fill-gold" :style="{ width: (cnt / maxAcquisitionChannel) * 100 + '%' }"></div>
                </div>
                <div class="w-10 text-right text-sm">{{ cnt }}</div>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 mt-2">No acquisition data available.</div>
          </div>
        </div>

        <!-- Goals & Targets -->
        <div class="glass-card">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-2xl font-semibold text-gold">Goals</h2>
            <button class="glass-button" @click="saveGoals">Save</button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="text-sm text-gray-300">Weekly Installs Target</label>
              <input type="number" v-model.number="goals.weeklyInstallsTarget" class="w-full glass-input" />
            </div>
            <div>
              <label class="text-sm text-gray-300">Activation Rate Target (%)</label>
              <input type="number" step="0.1" v-model.number="goals.activationRateTargetPct" class="w-full glass-input" />
            </div>
            <div>
              <label class="text-sm text-gray-300">WAU Target</label>
              <input type="number" v-model.number="goals.wauTarget" class="w-full glass-input" />
            </div>
            <div>
              <label class="text-sm text-gray-300">Monthly Bookers Target (%)</label>
              <input type="number" step="0.1" v-model.number="goals.monthlyBookersTargetPct" class="w-full glass-input" />
            </div>
            <div>
              <label class="text-sm text-gray-300">GMV Target (£)</label>
              <input type="number" step="0.01" v-model.number="goals.gmvTarget" class="w-full glass-input" />
            </div>
            <div>
              <label class="text-sm text-gray-300">Net Revenue Target (£)</label>
              <input type="number" step="0.01" v-model.number="goals.netRevenueTarget" class="w-full glass-input" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.font-inter { font-family: "Inter", sans-serif; }
.text-gold { color: #C59C2F; }

.dashboard-root {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at -10% -10%, rgba(197,156,47,0.15), transparent 40%),
              radial-gradient(1000px 600px at 110% 0%, rgba(0,122,255,0.12), transparent 40%),
              linear-gradient(180deg, #0b0b0b 0%, #121212 100%);
  position: relative;
}

.glass-card {
  border-radius: 18px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
}

.stat-card {
  border-left: 2px solid rgba(197,156,47,0.35);
}

.glass-input {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.18);
  color: #fff;
  border-radius: 12px;
  padding: 6px 10px;
  outline: none;
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
}

.glass-button {
  padding: 8px 14px;
  border-radius: 12px;
  color: #0b0b0b;
  background: linear-gradient(180deg, #FFD977 0%, #C59C2F 100%);
  border: 1px solid rgba(255,255,255,0.25);
  box-shadow: 0 8px 18px rgba(197,156,47,0.25);
}

.glass-track {
  height: 8px;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.16);
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.45);
}
.glass-fill-blue {
  height: 8px;
  border-radius: 9px 0 0 9px;
  background: linear-gradient(90deg, rgba(0,122,255,0.9) 0%, rgba(0,122,255,0.6) 100%);
}
.glass-fill-green {
  height: 8px;
  border-radius: 9px 0 0 9px;
  background: linear-gradient(90deg, rgba(52,199,89,0.95) 0%, rgba(52,199,89,0.6) 100%);
}
.glass-fill-gold {
  height: 8px;
  border-radius: 9px 0 0 9px;
  background: linear-gradient(90deg, rgba(197,156,47,0.95) 0%, rgba(197,156,47,0.6) 100%);
}
</style>
